import type { AiLimitPayload } from '../components/ai/aiLimitEvents';
export type WellbeingDot = 'ok' | 'watch' | 'concern';
export type EngagementDot = 'active' | 'idle' | 'silent';
export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AlertAudience = 'teachers' | 'counselor_only' | 'admin_only';
export type AlertStatus =
  | 'open' | 'acknowledged' | 'in_case' | 'resolved'
  | 'snoozed' | 'dismissed' | 'auto_dismissed';

export interface StudentListRow {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  grade_id: number | null;
  grade_name: string | null;
  section: { id: number; name: string; display: string } | null;
  engagement_dot: EngagementDot;
  wellbeing_dot: WellbeingDot;
  wellbeing_state?: string | null;
  last_active_at: string | null;
  open_alerts_count: number;
  risk_band: 'insufficient_data' | 'low' | 'medium' | 'high';
  risk_score: number | null;
  /** All-time average of the student's final school scores. Absent until the backend adds it. */
  avg_score?: number | null;
  /** `UserProfile.gender` ("male" / "female"). Absent until the backend adds it. */
  gender?: string | null;
  /** Teachers: why the student is theirs ('Homeroom', subject names); null for other roles. */
  teaching?: string[] | null;
  /** Largest positive risk sub-score. Absent until the backend adds it. */
  risk_top_signal?: 'trend' | 'recency' | 'weak_chapters' | 'engagement' | null;
}

// Spec B-4 §3.7 — weekly_summary block on StudentOverview
export type SummaryBulletTone = 'strength' | 'concern' | 'suggestion';

export interface WeeklySummary {
  headline: string;
  bullets: Array<{ tone: SummaryBulletTone; text: string }>;
  generated_at: string;
  week_of: string | null;
}

// Spec C-1 §4.1 — anomalies block on StudentOverview
export type AnomalyType = 'drop' | 'spike' | 'silence';

export interface Anomaly {
  id: number;
  anomaly_type: AnomalyType;
  severity: 'info' | 'warning';
  description: string;
  detected_at: string;
  window_start: string | null;
  window_end: string | null;
  resolved_at: string | null;
}

// Spec B-5 §4.1 — study_plan block on StudentOverview
export interface StudyPlanChapter {
  chapter_id: number;
  name: string;
  subject: string;
  rationale: string;
}

export interface StudyPlan {
  chapters: StudyPlanChapter[];
  generated_at: string;
  week_of: string;
}

// Spec B-1 §3.6 — risk block on StudentOverview
export interface RiskBlock {
  score: number;
  band: 'low' | 'medium' | 'high';
  signals: {
    trend: number;
    recency: number;
    weak_chapters: number;
    engagement: number;
  };
  last_computed_at: string | null;
}

export interface StudentsListResponse { results: StudentListRow[]; }

export interface StudentOverview {
  profile: {
    username: string; first_name: string; last_name: string;
    gender: string | null; region: string | null; language_pref: string;
    motivation: string | null; learning_style: string | null;
    target_universities: string[]; target_departments: string[];
    school_grade: string | null;
    /** Roll number (paper exams). Absent until the backend adds it; null when unassigned. */
    roll_number?: number | null;
    /** Account creation time. Absent until the backend adds it. */
    date_joined?: string | null;
    /** The school's designated counsellor. Absent until the backend adds it; null when none is set. */
    counselor?: { id: number; name: string } | null;
  };
  section: { id: number; name: string; display: string } | null;
  kpis: {
    last_active_at: string | null;
    engagement_dot: EngagementDot;
    wellbeing_dot: WellbeingDot;
    avg_score: number | null;
  };
  open_alerts: AlertInstance[];
  risk: RiskBlock | null;
  weekly_summary: WeeklySummary | null;
  study_plan: StudyPlan | null;
  anomalies: Anomaly[];
}

export interface AcademicResultRow {
  id: number; created: string;
  subject: string | null; chapter: string | null;
  exam_name: string | null;
  score_percentage: number | null;
  right: number; wrong: number; unanswered: number;
  source?: ResultSource;
  /** Submitted after the answer key was released (not counted in class averages). */
  late?: boolean;
  /** Late, and the taken time could not be verified (no server-time anchor). */
  late_unverified?: boolean;
}

export interface CommitmentsResponse {
  active: Array<{
    id: number; kind: string; target_value: number;
    deadline: string; milestones_fired: number[];
    last_progress_at: string | null; last_progress_value: number;
    started_at: string;
  }>;
  history: Array<{
    id: number; kind: string; target_value: number;
    deadline: string; status: string; completed_at: string | null;
    /** When the commitment started. Absent until the backend adds it. */
    started_at?: string | null;
    /** Last recorded progress value. Absent until the backend adds it; null when nothing was recorded. */
    last_progress_value?: number | null;
  }>;
  current_week_start: string | null;
  plan_items: Array<{
    id: number; day: string; subject_name: string;
    status: 'pending' | 'completed' | 'skipped';
    /** Planned minutes. Absent until the backend adds it. */
    estimated_minutes?: number | null;
    chapter_id?: number | null;
  }>;
}

export interface WellbeingFlagOnly {
  wellbeing_dot: WellbeingDot;
  has_open_case: boolean;
  message: string;
}

export interface WellbeingRaw {
  wellbeing_dot: WellbeingDot;
  latest_state: string | null;
  last_checkin_at: string | null;
  career_aspiration_text: string;
  reflections_seen: Record<string, string>;
  checkin_history: Array<{ created_at: string; payload: Record<string, unknown> }>;
}

export type WellbeingResponse = WellbeingFlagOnly | WellbeingRaw;
export function isWellbeingRaw(r: WellbeingResponse): r is WellbeingRaw {
  return 'checkin_history' in r;
}

export interface StudentCasesResponse {
  cases: Array<{
    id: number; status: string; category: string;
    opened_at: string; closed_at: string | null;
    outcome: string | null;
  }>;
  notes: Array<{
    id: number; body: string; pinned: boolean; visibility: string;
    created_at: string; author_username: string | null; author_name?: string | null;
  }>;
}

export interface AlertInstance {
  id: number; rule_code: string; rule_name: string;
  severity: AlertSeverity; audience: AlertAudience; status: AlertStatus;
  payload: Record<string, unknown>; context: Record<string, unknown>;
  fired_at: string; snoozed_until: string | null; resolved_at: string | null;
  student_id: number; student_username: string; student_name?: string;
  /** Absent until the backend adds them (fidelity round 2). */
  student_grade?: string | null;
  student_section?: { id: number; name: string; display: string } | null;
  student_risk_score?: number | null;
  /** The case this alert was linked into; absent until the backend sends it. */
  case_id?: number | null;
  /** Detail endpoint only: staff this alert was pushed to. */
  notified?: Array<{ user_id: number; name: string; sent_at: string }>;
}

export interface InterventionCase {
  id: number; student: number; student_name: string; status: string; category: string;
  visibility: string; outcome: string | null; outcome_note?: string;
  opened_at: string; closed_at: string | null;
  opened_by_username: string | null; opened_by_name?: string | null;
  /** Optional until the backend adds them; null when unset. */
  student_grade?: string | null;
  student_section?: { id: number; name: string; display: string } | null;
  last_activity_at?: string | null;
  last_event?: { id: number; kind: string; body: string; payload: Record<string, unknown>; created_at: string } | null;
}

/** An alert linked into a case (case detail only). */
export interface CaseLinkedAlert {
  id: number; rule_code: string; rule_name: string; severity: string; status: string; fired_at: string;
}

export interface CaseDetailResponse {
  case: InterventionCase; events: CaseEvent[];
  /** Optional until the backend adds it. */
  linked_alerts?: CaseLinkedAlert[];
}

export interface CaseEvent {
  id: number; case: number; kind: string; body: string;
  payload: Record<string, unknown>; actor_username: string | null; actor_name?: string | null;
  created_at: string;
}

export interface StudentNote {
  id: number; student: number; body: string;
  visibility: 'teachers_of_student' | 'counselor_admin_only';
  pinned: boolean; author_username: string | null; author_name?: string | null;
  created_at: string; updated_at: string;
}

export interface TeacherAssignment {
  id: number; teacher: number; teacher_username: string; teacher_name?: string;
  institution: number; school_grade: number; school_grade_name: string;
  subject: number | null; subject_name: string | null;
  is_homeroom: boolean;
  /** The section the assignment is scoped to, when it is not grade-wide. */
  section?: { id: number; name: string } | null;
  /** When the assignment was created. Absent until the backend adds it. */
  created_at?: string;
}

// Analytics (Phase 2-B, spec §7.7)
export interface AnalyticsGradeRow {
  id: number;
  name: string;
  student_count: number;
  /** Subjects linked to the grade for this school; absent on older backends. */
  subject_count?: number;
  /** False when the viewer (a section teacher) may not open the whole grade. */
  whole_grade?: boolean;
  /** Subjects the viewer may see for the whole grade; null/absent = all. */
  subjects?: number[] | null;
  sections: Array<{
    id: number;
    name: string;
    display: string;
    student_count: number;
    /** Subjects the viewer may see in this section; null/absent = all. */
    subjects?: number[] | null;
  }>;
}
export interface AnalyticsGradesResponse {
  grades: AnalyticsGradeRow[];
}

export interface AnalyticsAcademicSeriesRow {
  week_start: string;
  avg_score: number | null;
  median_score: number | null;
  p25: number | null;
  p75: number | null;
  attempts_count: number;
  students_with_data: number;
}
export interface AnalyticsStrugglingChapter {
  chapter_id: number;
  chapter_name: string;
  avg_score: number;
  attempts: number;
}
export interface AnalyticsAcademicResponse {
  week_starts: string[];
  cohort_size: number;
  subject_name: string;
  series: AnalyticsAcademicSeriesRow[];
  top_struggling_chapters: AnalyticsStrugglingChapter[];
}

export interface AnalyticsEngagementResponse {
  engagement_dot_counts: { active: number; idle: number; silent: number };
  avg_study_minutes_7d: number;
  avg_study_minutes_30d: number;
  daily_active_series: Array<{ date: string; count: number }>;
}

export interface AnalyticsWellbeingResponse {
  dot_counts: { ok: number; watch: number; concern: number };
  latest_state_counts: Record<string, number>;
  open_cases_count: number;
  escalated_cases_count: number;
}

export interface AnalyticsInterventionsResponse {
  open_cases: number;
  in_progress_cases: number;
  escalated_cases: number;
  resolved_30d: number;
  auto_dismissed_30d: number;
  avg_time_to_resolve_hours: number | null;
  cases_by_category: Record<string, number>;
  open_alerts_by_severity: { critical: number; warning: number; info: number };
}

// Student 360 Tab 4 (Engagement & Behavior, spec §3.1, §4.5)
export interface StudentEngagementResponse {
  kpis: {
    total_study_minutes_7d: number;
    total_study_minutes_30d: number;
    avg_minutes_per_active_day: number;
    total_exams_taken: number;
    preferred_study_time: string | null;
  };
  daily_minutes_series: Array<{
    date: string;
    minutes: number;
    personal_baseline: number | null;
  }>;
  feature_usage: Record<string, number>;
  ai_usage: {
    input_tokens_used: number;
    output_tokens_used: number;
    total_spent_usd: number;
  } | null;
}

// Student 360 Tab 6 (Interests & Future, spec §3.2, §4.5)
export interface PredictedCareer {
  key: string;
  label_en?: string;
  score: number;
  cluster?: string;
  stream?: string;
  departments: string[];
  why?: { strong_subjects: string[]; top_interests: string[] };
  description_en?: string;
  typical_entrance_score_range?: string;
}

export interface ScholarshipMatch {
  id: number;
  name: string;
  provider: string;
  deadline: string | null;
  application_url: string;
  is_need_based: boolean;
  score: number;
  reasons: string[];
}

export interface StudentInterestsResponse {
  predicted_careers: PredictedCareer[];
  predicted_departments: string[];
  interest_scores: Record<string, number>;
  target_aspirations: { universities: string[]; departments: string[] };
  extracurriculars: string[];   // JSONField list -- backend returns []
  career_confidence: number | null;
  scholarship_matches: ScholarshipMatch[];
}

// Phase 2-D exam authoring (spec §5.4)
export type ExamAssignmentScope = 'grade' | 'grade_subject' | 'individual';

// Assignment fields carried by exam list/detail responses (Phase 1b-ii).
export interface ExamAssignFields {
  due_date?: string | null;
  due_at?: string | null;
  instructions?: string;
  assignment_scope?: ExamAssignmentScope;
  assigned_student_ids?: number[];
  assigned_section_ids?: number[];
  assigned_sections?: { id: number; name: string }[];
}

// Phase 2-F Settings → Alerts (spec §4.6)
export interface AlertRuleSettingsRow {
  code: string;
  name: string;
  description: string;
  default_severity: string;
  default_audience: string;
  default_threshold_config: Record<string, unknown>;
  is_enabled: boolean;
  threshold_overrides: Record<string, unknown>;
  updated_at: string | null;
  updated_by_username: string | null; updated_by_name?: string | null;
}

export interface AlertRulesListResponse {
  rules: AlertRuleSettingsRow[];
  /** Every alert fired for the school's students in the last 7 days. Absent on older backends. */
  stats_7d?: { total: number; critical: number; open: number };
  /** Section-level override rows and the sections that have one. Absent on older backends. */
  section_overrides?: { count: number; sections: string[] };
}

// Phase 2-E Audit-of-viewers (spec §E.4)
export interface StudentViewerRow {
  viewer_username: string;
  viewer_name?: string;
  viewer_role: string | null;
  tab: string;
  viewed_at: string; // ISO
}

export interface StudentViewersResponse {
  viewers: StudentViewerRow[];
}

// Phase 2.5-A Sections (spec §6)
export interface SectionTeacherSummary {
  id: number;
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
}

export interface Section {
  id: number;
  school_grade: number;
  name: string;
  display: string;
  student_count: number;
  homeroom_teacher: SectionTeacherSummary | null;
  created_at: string;
}

export interface SectionsListResponse {
  sections: Section[];
}

export interface SectionRosterEntry {
  id: number;
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
}

export interface SectionRosterResponse {
  students: SectionRosterEntry[];
}

export interface BulkOpResponse {
  moved?: number;
  sent?: number;
  errors: Array<{ student_id: number; reason: string }>;
}

// Phase 2.5-B response types

export interface HistogramBucket { bucket: string; count: number; }

export interface ClassPerfOverviewResponse {
  cohort_size: number;
  students_with_data: number;
  attempts_count: number;
  avg_score: number | null;
  median_score: number | null;
  p25: number | null;
  p75: number | null;
  histogram: HistogramBucket[];
  /** The same average over the equal-length window just before this one. Absent until the backend adds it. */
  prior_avg_score?: number | null;
  prior_students_with_data?: number;
}

export interface ClassPerfBySubjectRow {
  id: number;
  name: string;
  avg_score: number | null;
  attempts_count: number;
  students_with_data: number;
}
export interface ClassPerfBySubjectResponse {
  week_start: string | null;
  subjects: ClassPerfBySubjectRow[];
}

export interface ClassPerfExamRow {
  id: number;
  name: string;
  subject: { id: number; name: string } | null;
  scope: 'school' | 'national' | 'practice';
  created: string;
  students_took: number;
  avg_score: number;
  avg_duration_minutes: number | null;
  score_distribution: Array<{ bucket: string; count: number }>;
  delta_vs_prior_on_same_subject: number | null;
  delivery?: ExamDelivery;
}
export interface ClassPerfExamsResponse { exams: ClassPerfExamRow[]; }

export interface ClassPerfTrendPoint {
  week_start: string;
  avg_score: number | null;
  attempts_count: number;
  students_with_data: number;
}
export interface ClassPerfTrendsResponse {
  subject_name: string;
  series: ClassPerfTrendPoint[];
}

export interface ClassPerfLeaderboardSection {
  id: number;
  name: string;
  display: string;
  avg_score: number | null;
  students_with_data: number;
  attempts_count: number;
  rank: number | null;
  trend: Array<{ week_start: string; avg_score: number | null }>;
}
export interface ClassPerfLeaderboardResponse {
  week_start: string | null;
  /** Null for the grade-level "all subjects" ranking (`subject=all`). */
  subject: { id: number; name: string } | null;
  sections: ClassPerfLeaderboardSection[];
}

// Phase 2.5-C response types

export interface ChapterMasteryCell {
  student_id: number;
  chapter_id: number;
  pct: number;
  attempts: number;
}

export interface ChapterMasteryStudent {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  avg: number | null;
  /** The student's section. Absent until the backend adds it. */
  section?: { id: number; name: string; display: string } | null;
}

export interface ClassPerfChapterMasteryResponse {
  chapters: Array<{ id: number; name: string }>;
  students: ChapterMasteryStudent[];
  cells: ChapterMasteryCell[];
  class_avg_per_chapter: Array<{
    chapter_id: number;
    pct: number | null;
    attempts: number;
  }>;
  pass_score_pct: number;
}

export interface QuestionOption {
  label: string;
  text: string;
  count: number;
  is_correct: boolean;
}

export interface ClassPerfQuestionRow {
  id: number;
  ques_excerpt: string;
  ques_html: string;
  subject: string | null;
  chapter: string | null;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer';
  correct_ans: string | null;
  attempts: number;
  wrong_count: number;
  pct_wrong: number;
  avg_time_seconds: number | null;
  options: QuestionOption[];
}

export interface ClassPerfQuestionsResponse {
  questions: ClassPerfQuestionRow[];
}

export interface ClassPerfSyllabusEntry {
  id: number | null;
  subject: string | null;
  subject_id: number | null;
  chapter: string;
  chapter_id: number;
  taught_by_name: string | null;
  notes: string;
}

export interface ClassPerfSyllabusWeek {
  week_start: string;
  source: 'teacher' | 'inferred' | 'empty';
  entries: ClassPerfSyllabusEntry[];
}

export interface ClassPerfSyllabusResponse {
  weeks: ClassPerfSyllabusWeek[];
}

export interface SyllabusEntry {
  id: number;
  section: number;
  subject: number;
  subject_name: string;
  chapter: number;
  chapter_name: string;
  week_start: string;
  taught_by_name: string | null;
  notes: string;
}

export interface SyllabusEntriesListResponse {
  entries: SyllabusEntry[];
}

// Phase 2.5-D student academic types

export interface StudentWeekPoint {
  week_start: string;
  avg_score: number;
  attempts_count: number;
}

export interface StudentBySubjectSeries {
  subject_id: number;
  subject_name: string;
  series: StudentWeekPoint[];
}

export interface StudentByChapterSeries {
  chapter_id: number;
  chapter_name: string;
  subject_id: number | null;
  subject_name: string | null;
  series: StudentWeekPoint[];
}

export interface StudentWeeklyTrendResponse {
  overall: StudentWeekPoint[];
  by_subject: StudentBySubjectSeries[];
  by_chapter: StudentByChapterSeries[];
}

export interface StudentChapterMasteryResponse {
  chapters: Array<{ id: number; name: string }>;
  student_cells: Array<{ chapter_id: number; pct: number; attempts: number }>;
  class_avg_per_chapter: Array<{ chapter_id: number; pct: number | null; attempts: number }>;
  student_overall_avg: number | null;
  class_overall_avg: number | null;
}

export interface StudentMissedQuestionsResponse {
  questions: ClassPerfQuestionRow[];
}

// Phase 2.5-E grouping + section alert override types

export interface AutoGroupStudent {
  id: number;
  username: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  overall_avg?: number | null;
  pct?: number;
  attempts?: number;
}

export interface AutoBand {
  band: 'struggling' | 'on_track' | 'advanced';
  cutoff: string;
  students: AutoGroupStudent[];
}

export interface ChapterFocusGroup {
  subject_id: number;
  subject_name: string;
  chapter_id: number;
  chapter_name: string;
  threshold_pct: number;
  students: AutoGroupStudent[];
}

export interface AutoGroupsResponse {
  overall_bands: AutoBand[];
  chapter_focus: ChapterFocusGroup[];
}

export interface StudentGroup {
  id: number;
  section_id: number;
  name: string;
  notes: string;
  created_at: string;
  updated_at: string;
  members: Array<{ id: number; username: string; first_name: string; last_name: string }>;
  /** Teacher grouping: the subject the group was made for (null = any subject). */
  subject?: { id: number; name: string } | null;
  /** '' for a hand-made group, else the suggestion lens it came from. */
  lens?: GroupingLens | '';
  criteria?: GroupCriteria;
  refreshed_at?: string | null;
  created_by_name?: string | null;
  /** Whether the viewer may edit, refresh or delete it. */
  can_edit?: boolean;
  size?: number;
}

export type GroupingLens = 'chapter' | 'level' | 'readiness';

/** What a saved group re-runs on Refresh. */
export interface GroupCriteria {
  lens?: GroupingLens;
  band?: string;
  chapter_id?: number | null;
  weeks?: number;
}

export interface GroupingStudent {
  id: number;
  name: string;
  value: number | null;
  evidence: number | null;
  /** "9/22 correct (41%)". */
  reason: string;
}

export interface GroupingBand {
  key: string;
  label: string;
  description: string;
  students: GroupingStudent[];
}

export interface GroupingSuggestionsResponse {
  lens: GroupingLens;
  grade_id: number;
  section_id: number;
  subject_id: number;
  weeks: number;
  pass_pct: number;
  available: boolean;
  message: string | null;
  /** Chapter lens only: chapters with attempts in the window, weakest first. */
  chapters?: Array<{ id: number; name: string; class_pct: number | null; attempts: number }> | null;
  chapter_id?: number | null;
  /** Readiness lens only. */
  national_exam?: { id: number; name: string } | null;
  groups: GroupingBand[];
}

export interface StudentGroupRefreshResponse {
  group: StudentGroup;
  added: number;
  removed: number;
}

/** A group the viewer may give work to (student-groups/?grade&subject). */
export interface AssignableGroup {
  id: number;
  name: string;
  section: { id: number; name: string };
  subject_id: number | null;
  member_ids: number[];
  size: number;
}

export interface AssignableGroupsResponse {
  groups: AssignableGroup[];
}

export interface StudentGroupWriteBody {
  name: string;
  notes?: string;
  member_ids: number[];
  subject_id?: number | null;
  lens?: GroupingLens | '';
  criteria?: GroupCriteria;
}

export interface StudentGroupsListResponse {
  groups: StudentGroup[];
}

export interface SectionAlertResolution {
  rule_code: string;
  is_enabled: boolean;
  threshold_config: Record<string, unknown>;
  source_per_field: {
    is_enabled: 'section' | 'institution' | 'default';
    threshold_config: Record<string, 'section' | 'institution' | 'default'>;
  };
}

export interface SectionAlertOverridesListResponse {
  rules: SectionAlertResolution[];
}

// Spec B-6a §6.2 / coach v2 — coach chat types
export type CoachScopeType = 'student' | 'section' | 'grade' | 'school' | 'exam' | 'mine';

export interface CoachThread {
  id: number;
  title: string;
  scope_type: CoachScopeType;
  scope_id: number | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  message_count: number;
}

/** Where a source or scope lives; each client maps it to its own route. */
export interface CoachRef {
  student_id?: number; result_id?: number; exam_id?: number;
  grade_id?: number | null; section_id?: number | null; tab?: string;
}
export interface CoachSource { kind: 'student' | 'exam_result' | 'exam' | 'class'; label: string; ref: CoachRef }

export type CoachAction =
  | { type: 'create_note'; student_id: number; student_name: string; body: string; visibility: 'teachers_of_student' | 'counselor_admin_only' }
  | { type: 'open_case'; student_id: number; student_name: string; category: string; summary: string }
  | { type: 'draft_parent_message'; student_id: number; student_name: string; text: string }
  | { type: 'start_remediation'; grade_id: number; section_id: number | null; subject_id: number | null; chapters: string[] };

export interface CoachMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  status?: 'ok' | 'error' | 'interrupted';
  sources?: CoachSource[];
  suggestions?: string[];
  actions?: CoachAction[];
  feedback?: -1 | 0 | 1;
  feedback_note?: string;
  language?: string;
}

export interface CoachThreadsResponse { threads: CoachThread[]; }
export interface CoachMessagesResponse { thread_id: number; has_more?: boolean; messages: CoachMessage[]; }
export interface CoachSendResponse {
  user_message: CoachMessage;
  assistant_message: CoachMessage | null;
  error?: string;
}

export interface CoachStarter { label: string; prompt: string; kind: 'question' | 'report' }
export interface CoachStartersResponse {
  scope: { type: CoachScopeType; id: number | null; label: string; ref: CoachRef };
  starters: CoachStarter[];
}

export type CoachLanguage = 'en' | 'am' | 'om';
export type CoachReport = 'weekly_brief' | 'meeting_summary';

/** One typed event from the coach stream. */
export type CoachStreamEvent =
  | { type: 'meta'; thread_id: number; user_message_id: number }
  | { type: 'tool'; name: string; label: string }
  | { type: 'delta'; text: string }
  | { type: 'sources'; sources: CoachSource[] }
  | { type: 'suggestions'; suggestions: string[] }
  | { type: 'actions'; actions: CoachAction[] }
  | { type: 'done'; message: CoachMessage; usage: { prompt: number; completion: number } }
  | { type: 'error'; code: string; message: string; message_id?: number; ai_limit?: AiLimitPayload };

export interface CoachAuditEvent {
  id: number; user: { id: number; name: string };
  scope_type: CoachScopeType; scope_id: number | null; scope_label: string;
  question: string; tools_called: string[]; status: string; created_at: string;
}
export interface CoachAuditResponse { count: number; page: number; page_size: number; events: CoachAuditEvent[] }

// Spec B-6c §4.1 / coach v2 — what a coach chat is about
export type CoachScope =
  | { type: 'student'; id: number; label?: string }
  | { type: 'section'; id: number; label?: string }
  | { type: 'grade'; id: number; label?: string }
  | { type: 'exam'; id: number; label?: string }
  | { type: 'school'; id?: null; label?: string }
  | { type: 'mine'; id?: null; label?: string };

// Phase 3A §5.5.3, §10.1 — designated counselor / institution settings
export interface DesignatedCounselor {
  profile_id: number;
  username: string;
  first_name: string;
  last_name: string;
}

export interface InstitutionSettingsResponse {
  designated_counselor: DesignatedCounselor | null;
}

// Phase 3B — Parent digest admin list + Student-360 Parent tab (spec §6.2.10, §7.2 Tab 8)
export type ParentDigestStatus = 'not_generated' | 'pending' | 'sent' | 'opened' | 'failed' | 'opted_out';

export interface ParentDigestRow {
  id: number | null;
  student_id: number;
  student_name: string;
  parent_name: string;
  parent_email: string;
  telegram_linked: boolean;
  week_start: string;
  status: ParentDigestStatus;
  sent_at: string | null;
  sent_channels: string[];
  /** Optional until the backend adds them; null when unset. */
  grade_name?: string | null;
  section_display?: string | null;
}

export interface ParentDigestSummary {
  consented: number;
  sent: number;
  failed: number;
  opted_out: number;
}

export interface ParentDigestListResponse {
  results: ParentDigestRow[];
  summary: ParentDigestSummary;
}

export interface ParentDigestAreaToSupport {
  chapter: string | null;
  subject: string | null;
  mastery_pct: number | null;
}

export interface ParentDigestUpcomingExam {
  name: string;
  subject: string | null;
  due_date: string;
}

export interface ParentDigestPayload {
  student_name: string;
  week_start: string;
  hours_studied: number;
  strongest_subject: string | null;
  areas_to_support: ParentDigestAreaToSupport[];
  upcoming_exams: ParentDigestUpcomingExam[];
  encouragement_am: string;
  encouragement_en: string;
}

export interface StudentParentLatestDigest {
  week_start: string;
  status: ParentDigestStatus;
  sent_at: string | null;
  sent_channels: string[];
  /** Who it went to, one entry per guardian (absent on digests sent before guardians). */
  recipients?: GuardianRecipient[];
  payload: ParentDigestPayload;
}

export interface GuardianRecipient { guardian_id: number; name: string; channels: string[] }

export type GuardianRelationship = 'mother' | 'father' | 'guardian' | 'other';

/** One of a student's parents/guardians (up to `max_guardians`). */
export interface Guardian {
  id: number;
  name: string;
  relationship: GuardianRelationship;
  email: string;
  phone: string;
  /** Who added it: the student (digest follows their consent) or the school (its own toggle). */
  source: 'student' | 'school';
  /** The student's own contact from the app. */
  is_primary: boolean;
  receives_digest: boolean;
  /** Whether this guardian gets the weekly digest right now. */
  digest_on: boolean;
  opted_out: boolean;
  telegram_linked: boolean;
  /** Personal Telegram link; only for staff who can edit guardians, '' once linked. */
  telegram_link_url?: string;
  added_by_name: string;
  created_at: string;
}

export interface GuardianInput {
  name?: string;
  relationship?: GuardianRelationship;
  email?: string;
  phone?: string;
  receives_digest?: boolean;
}

export interface StudentParentContactEvent {
  id: number;
  created_at: string;
  body: string;
  payload: Record<string, unknown>;
  actor_username: string | null; actor_name?: string | null;
}

export interface StudentParentResponse {
  parent_name: string;
  parent_email: string;
  parent_digest_consent: boolean;
  telegram_linked: boolean;
  latest_digest: StudentParentLatestDigest | null;
  /** `hours_studied` of the digest before `latest_digest`; absent or null when there is none. */
  previous_digest_hours_studied?: number | null;
  contact_log: StudentParentContactEvent[];
  guardians?: Guardian[];
  /** School admins, counsellors and the student's homeroom teacher. */
  can_edit_guardians?: boolean;
  max_guardians?: number;
}

// Assessment loop (2026-09-21 plan B, Task 1) — exam report + cycle types
export type ExamScope = 'school' | 'national' | 'practice';
export interface ReportOption { label: 'A'|'B'|'C'|'D'; text: string; count: number; is_correct: boolean }
export interface ReportQuestionRow {
  question_id: number; order: number; chapter_id: number | null; chapter_name: string | null;
  excerpt: string; attempts: number; pct_correct: number | null; discrimination: number | null;
  avg_time_sec: number | null; options: ReportOption[];
  /** Paper: answered in writing and marked by the teacher; options is empty. */
  graded_by_teacher?: boolean;
  predicted_difficulty: number | null; observed_difficulty: number | null;
  quality_score: number | null; flags: string[]; suspect: boolean; suspect_reasons: string[];
  /** Written questions (3c-i): marked by the teacher, so no option bars. */
  type?: string; max_marks?: number; avg_marks?: number | null; to_mark?: number;
}
export interface ReportSectionRow {
  id: number; name: string; display?: string; took: number; assigned: number;
  avg_score: number | null; pass_rate: number | null;
}
export interface ReportChapterRow {
  chapter_id: number; chapter_name: string; question_count: number; class_avg: number | null;
  below_pass: Array<{ student_id: number; name: string; pct: number }>;
}
export interface ReportStudentRow {
  student_id: number; name: string;
  /** null while the result awaits marking of written answers. */
  score: number | null; rank: number | null; time_minutes: number | null;
  weakest_chapter_id: number | null;
  /** Null when unsectioned; absent on older backends. */
  section?: { id: number; name: string } | null;
  source?: ResultSource;
  comment?: ResultComment | null;
  awaiting_marking?: boolean;
  /** Submitted after the answer key was released: unranked, not in class averages. */
  late?: boolean;
  /** Late, and the taken time could not be verified (no server-time anchor). */
  late_unverified?: boolean;
  /** Staff-only attempt evidence; null/absent when there is none (older backends, paper results). */
  integrity?: ReportIntegrity | null;
}
export interface ReportIntegrity {
  leaves: number; away_s: number; multi_window: boolean; over_time: boolean; start_source: string;
}
export interface StartedNotSubmittedRow { student_id: number; name: string; started_at: string }
export type ResultsReleaseMode = 'immediate' | 'after_due' | 'manual';
export interface ResultComment { body: string; author: string; updated_at: string }
export interface ResultsReleaseState {
  results_release: ResultsReleaseMode; results_released_at: string | null; released: boolean;
}
export interface ExamReportResponse {
  exam: { id: number; name: string; subject: { id: number; name: string } | null;
          grade: { id: number; name: string } | null; scope: ExamScope; created: string;
          is_generated: boolean; question_count: number; delivery?: ExamDelivery; due_at?: string | null };
  results_release?: ResultsReleaseMode; results_released_at?: string | null; released?: boolean;
  header: { assigned_count: number; took_count: number; avg_score: number | null; median_score: number | null;
            pass_score_pct: number; pass_rate: number | null; avg_duration_minutes: number | null;
            delta_vs_prior_on_same_subject: number | null; awaiting_marking_count?: number;
            /** Late submissions listed but left out of every statistic. */
            late_count?: number;
            /** Whole-grade report only: per-section comparison (absent on older backends). */
            sections?: ReportSectionRow[] };
  questions: ReportQuestionRow[]; chapters: ReportChapterRow[]; students: ReportStudentRow[];
  started_not_submitted?: StartedNotSubmittedRow[];
}
export type CycleStatus = 'diagnosed' | 'assigned' | 'retested' | 'closed';
export interface BaselineCell { pct: number; attempts: number }
export interface GainCell { after_pct: number; after_attempts: number; delta: number | null }
export interface CycleGain {
  computed_at?: string;
  cells?: Record<string, Record<string, GainCell>>;
  students?: Record<string, { mean_delta: number | null; crossed_pass: boolean; retested: boolean }>;
  chapters?: Record<string, { class_after_avg: number | null; mean_delta: number | null }>;
  summary?: { mean_delta: number | null; students_with_after: number; students_retested: number;
              target_count: number; crossed_pass_share: number | null };
}
export interface AssessmentCycle {
  id: number; status: CycleStatus;
  source_exam: { id: number; name: string } | null;
  grade_id: number; section_id: number | null; subject: { id: number; name: string };
  scopes: ExamScope[];
  target_chapters: Array<{ chapter_id: number; count: number; name: string }>;
  target_student_ids: number[]; target_count: number;
  remedial_exam: { id: number; name: string } | null; remedial_exam_set_id: number | null;
  baseline_mastery: { computed_at?: string; pass_score_pct?: number; weeks?: number;
                      cells?: Record<string, Record<string, BaselineCell>> };
  gain: CycleGain; gain_computed_at: string | null; assigned_at: string | null; closed_at: string | null;
  created_at: string; blueprint: Record<string, unknown>; generation_job_id: number | null;
}
export interface CyclesListResponse { cycles: AssessmentCycle[] }
export interface CycleCreatePayload {
  source_exam_id?: number | null; grade_id: number; section_id?: number | null; subject_id: number;
  scopes: ExamScope[]; target_chapters: Array<{ chapter_id: number; count: number }>;
  target_student_ids: number[];
}
export interface CycleCreateResponse { cycle_id: number; blueprint: Record<string, unknown> }
export interface StudentCycleRow extends AssessmentCycle {
  student_gain: { mean_delta: number | null; crossed_pass: boolean; retested: boolean } | null;
  student_cells: Record<string, { baseline: BaselineCell | null; after: GainCell | null }>;
}

// School dashboard — academic payload (GET /api/school/dashboard/academic/)
export interface DashboardBlock {
  students: number; students_with_data: number; attempts: number;
  avg_score: number | null; pass_rate: number | null; delta_4w: number | null;
  /** Points / percent change over the last 4 weeks; absent until the backend adds them. */
  pass_rate_delta_4w?: number | null;
  active_delta_4w?: number | null;
  students_active_4w?: number; students_active_prior_4w?: number;
}
export interface DashboardGradeRow extends DashboardBlock { id: number; name: string; trend: Array<number | null> }
export interface DashboardSubjectRow {
  id: number; name: string; avg_score: number | null; attempts: number; students_with_data: number; delta_4w: number | null;
}
export interface DashboardWeakChapter {
  chapter_id: number; name: string; subject_id: number | null; subject_name: string | null;
  class_avg: number; attempts: number; students: number; students_below_pass: number;
  /** Absent until the backend adds it. */
  grade_names?: string[];
}
export interface DashboardRecentExam {
  id: number; name: string; subject: string | null; grade: string | null; created: string;
  students_took: number; avg_score: number | null; pass_rate: number; delta_vs_prior: number | null;
  delivery?: ExamDelivery;
}
export interface DashboardMover { student_id: number; name: string; grade: string | null; before: number; after: number; delta: number }
export interface AcademicDashboardResponse {
  generated_at: string; weeks: number; window_start: string; pass_score_pct: number;
  headline: DashboardBlock & { exams_this_week: number; attempts_this_week: number; active_cycles: number; high_risk: number };
  weekly_trend: Array<{ week_start: string; avg_score: number | null; attempts: number; students: number }>;
  grades: DashboardGradeRow[];
  subjects: DashboardSubjectRow[];
  score_distribution: HistogramBucket[];
  weak_chapters: DashboardWeakChapter[];
  recent_exams: DashboardRecentExam[];
  cycles: { total: number; active: number; by_status: Record<string, number>; mean_gain: number | null; crossed_pass_share: number | null; students_enrolled?: number; students_improved?: number };
  risk_bands: { low: number; medium: number; high: number; not_assessed: number };
  movers: { improving: DashboardMover[]; declining: DashboardMover[] };
}

// Paper exams (2026-09-25 plan B, Task 1) — mirrors eltest_backend
// api/school_panel/{serializers/paper.py, views/paper_*.py} field for field.
export type ExamDelivery = 'app' | 'paper';
export type ResultSource = 'app' | 'paper' | 'manual';

// Roll numbers: GET/POST grades/<id>/roll-numbers/[assign/]
export interface RollNumberRow { student_id: number; name: string; section: string | null; roll_number: number | null }
export interface RollNumbersResponse {
  grade: { id: number; name: string };
  students: RollNumberRow[];
  count: number;
  unassigned: number;
}
export interface RollNumbersAssignResponse extends RollNumbersResponse { assigned: number }

// Drafts: paper-exams/drafts/
export type PaperDraftStatus = 'extracting' | 'ready' | 'failed' | 'saved';
export type AnswerLetter = 'A' | 'B' | 'C' | 'D';
/** Whether a paper exam's written (teacher-graded) questions count in its scores. */
export type PaperWritten = 'marked' | 'excluded';
/** A sheet row's answer: a letter, or '*' when the teacher marked it invalid (two bubbles filled; scored wrong). */
export type SheetAnswer = AnswerLetter | '*';
export const INVALID_ANSWER = '*';
export interface PaperQuestionItem {
  order?: number;
  text: string;
  options: string[];                       // always four entries, '' when empty
  suggested_answer: AnswerLetter | null;
  suggested_chapter_id: number | null;
  confidence: number;                      // 0..1
  graded_by_teacher: boolean;
}
export interface PaperDraftFile { path: string; name: string; /** Page count of the file. */ pages?: number }
export interface PaperDraft {
  id: number; status: PaperDraftStatus; name: string;
  grade: { id: number; name: string }; subject: { id: number; name: string };
  files: PaperDraftFile[];
  questions: PaperQuestionItem[];
  error: string;
  exam: { id: number; name: string; delivery: ExamDelivery } | null;
  created_by: { id: number; name: string };
  max_bubbled: number;
  created_at: string; updated_at: string;
}
export interface PaperDraftListRow {
  id: number; status: PaperDraftStatus; name: string;
  grade: { id: number; name: string }; subject: { id: number; name: string };
  question_count: number;
  exam: number | null;
  created_by: { id: number; name: string };
  created_at: string; updated_at: string;
}
export interface PaperDraftsListResponse { drafts: PaperDraftListRow[] }
export interface PaperDraftCreatePayload { grade: number; subject: number; name: string; files: File[] }
export interface PaperDraftPatchPayload { name?: string; questions?: PaperQuestionItem[] }
export interface PaperDraftSaveResponse {
  id: number; status: 'saved'; exam_id: number; question_count: number; bubbled_count: number;
}
/** 400 from POST drafts/<id>/save/: the offending question numbers. */
export interface PaperDraftSaveError { detail: string; questions: number[] }

// Delivery: POST exams/<id>/delivery/
export interface ExamDeliveryResponse { id: number; delivery: ExamDelivery }

// Sheets: exams/<id>/sheets/
export type SheetStatus =
  | 'queued' | 'reading' | 'matched' | 'needs_review' | 'duplicate' | 'error' | 'confirmed' | 'discarded';
export type SheetCounts = Record<Exclude<SheetStatus, 'discarded'> | 'total', number>;
export interface SheetPerson { id: number; name: string }
export interface SheetRow {
  id: number; status: SheetStatus;
  roll_number_read: number | null;
  student: SheetPerson | null;
  warnings: string[];
  answered: number; blank: number; multi: number;
  score_preview: { right: number; total: number } | null;
  preview_url: string | null;
  result_id: number | null;
  error: string;
  uploaded_by: SheetPerson | null;
  created_at: string; confirmed_at: string | null;
}
export interface SheetGeometry {
  width: number; height: number; radius: number; options: AnswerLetter[];
  rows: Record<string, Array<[number, number]>>;   // row -> [x, y] per option A..D
  roll: Array<Array<[number, number]>>;            // column -> [x, y] per digit 0..9
}
export interface SheetTeacherQuestion { id: number; text: string; max_score: number; score: number | null; /** The question's number on the paper. Absent on older backends. */ number?: number }
export interface SheetDetail extends SheetRow {
  /** paper_written: 'excluded' scores every sheet over its multiple-choice questions only. */
  exam: { id: number; name: string; delivery: ExamDelivery; paper_written?: PaperWritten };
  n_rows: number;
  /** Row -> the question number printed beside it (row 1 is Q11 when Q1-10 are written). Absent on older backends. */
  row_numbers?: Record<string, number>;
  answers: Record<string, SheetAnswer | null>;
  row_confidence: Record<string, number>;
  key: Record<string, string | null>;
  teacher_scores: Record<string, number>;
  teacher_questions: SheetTeacherQuestion[];
  image_url: string | null;
  /** Corner marks the teacher clicked (fractions of the photo, TL TR BR BL); absent on older backends. */
  manual_corners?: [number, number][] | null;
  geometry: SheetGeometry;
  /** Ids of the other non-discarded sheets on this exam matched to the same student. Absent on older backends. */
  duplicate_of?: number[];
}
export interface SheetsListResponse {
  exam: { id: number; name: string; delivery: ExamDelivery; n_rows: number; paper_written?: PaperWritten };
  scans: SheetRow[];
  counts: SheetCounts;
}
export interface SheetsUploadResponse { scans: SheetRow[]; counts: SheetCounts }
export interface SheetPatchPayload {
  answers?: Record<string, SheetAnswer | null | ''>;
  student_id?: number | null;
  teacher_scores?: Record<string, number>;
  discard?: boolean;
}
/** 409 from POST sheets/<scan_id>/confirm/. */
export interface SheetConfirmConflict { detail: string; code: string; needs_replace: boolean }
export interface SheetsConfirmMatchedResponse {
  confirmed: Array<{ id: number; result_id: number; student_id: number | null }>;
  skipped: Array<{ id: number; code: string; reason: string }>;
  counts: SheetCounts;
}

// Paper exams list (2026-09-25 plan B, Task 3) — GET /api/school/paper-exams/
export interface PaperExamListRow {
  id: number;
  name: string;
  subject: { id: number; name: string } | null;
  grade: { id: number; name: string } | null;
  question_count: number;
  sheets: { confirmed: number; total: number };
  last_scan_at: string | null;
  created: string;
  /** Non-discarded scans awaiting review (needs_review, duplicate, error). Absent on older backends. */
  sheets_needs_review?: number;
  /** Students in the exam's audience, the denominator for "confirmed / expected". */
  sheets_expected?: number;
  /** Assigned section display names; empty means the whole grade. */
  sections?: string[];
  /** Mean of the official paper results, null when none. */
  average?: number | null;
}
export interface PaperExamsListResponse { exams: PaperExamListRow[] }

// School mode 1b-i: approve a join request with class details.
export type ApproveJoinRequestBody = {
  grade_id?: number;
  section_id?: number | null;
  roll_number?: number | null;
  swap?: boolean;
};
export type ApproveJoinRequestResponse = {
  status: 'ok';
  student: {
    id: number;
    name: string;
    grade: { id: number; name: string } | null;
    section: { id: number; name: string } | null;
    roll_number: number | null;
  };
};

export interface Attachment { id: number; name: string; mime: string; size: number; url: string }
export interface TaskCounts { audience: number; done: number | null; late: number | null; missing: number | null; read: number | null }
export interface StaffSchoolTask {
  id: number; kind: 'exam' | 'notice'; title: string; body: string;
  grade: { id: number; name: string } | null; subject: { id: number; name: string } | null;
  exam: { id: number; delivery: 'app' | 'paper'; kind?: ExamKind } | null;
  audience: 'grade' | 'sections' | 'students'; audience_label: string; state: 'published' | 'archived';
  opens_at: string; due_at: string | null; created_by: string; created_by_id: number | null;
  counts: TaskCounts | null; can_archive: boolean;
  /** Written answers still to mark (0 for notices and paper exams). */
  to_mark?: number;
  attachments?: Attachment[];
}
export type TrackerStatus = 'done' | 'late' | 'awaiting_marking' | 'missing' | 'not_yet' | 'read' | 'unread';
export interface TaskTrackerRow { student_id: number; name: string; section: string; status: TrackerStatus; at: string | null }
export interface TaskTracker {
  task: StaffSchoolTask; rows: TaskTrackerRow[];
  last_nudge: { at: string; by: string; recipients: number } | null;
  nudge_count: number; can_nudge: boolean; nudge_available_at: string | null;
}
export interface TaskNudgeResult { sent: number; last_nudge: TaskTracker['last_nudge'] }
export interface SchoolTasksPage { tasks: StaffSchoolTask[]; next_page: number | null }
export interface CreateNoticeBody { title: string; body: string; audience: 'grade' | 'sections' | 'students'; grade?: number; section_ids?: number[]; student_ids?: number[] }

// ---- Worksheets / homework authoring (school mode phase 3a) ----
export type ExamKind = 'exam' | 'worksheet' | 'homework';
export type AuthoringQuestionType = 'multiple_choice' | 'fill_blank' | 'matching' | 'written';

export interface AuthoringQuestion {
  id: number;
  type: AuthoringQuestionType | string;
  text: string;
  options: string[];
  answer: 'A' | 'B' | 'C' | 'D' | null;
  blanks?: string[][];
  left?: string[];
  right?: string[];
  pairs?: [number, number][];
  max_marks?: number;
  model_answer?: string;
  notes?: string;
  explanation: string;
  chapter?: { id: number; name: string } | null;
  reference?: string;
  /** Paper: answered in writing and marked by the teacher (no A–D options). */
  graded_by_teacher?: boolean;
}

export interface AuthoringExam {
  id: number;
  name: string;
  kind: ExamKind;
  status: 'draft' | 'published';
  questions: AuthoringQuestion[];
  attachments?: Attachment[];
  grade?: { id: number; name: string } | null;
  subject?: { id: number; name: string } | null;
  due_date?: string | null;
  /** false: another teacher created it, so this teacher may only view it (owner rule). */
  can_edit?: boolean;
}

// ---- A teacher's assignments (me/teaching/) and the "viewing as" switch ----
export interface MeTeachingRow { grade: { id: number; name: string }; section: { id: number; name: string } | null }
export interface MeTeaching {
  homeroom: MeTeachingRow[];
  subjects: (MeTeachingRow & { subject: { id: number; name: string } })[];
  /** Holds both a homeroom and a subject class: Homeroom / My subjects are offered. */
  can_switch: boolean;
  /** Each of the teacher's classes as a lens choice ('section:<id>' / 'grade:<id>'),
   *  with what they are there for ('Homeroom', subject names). */
  classes?: {
    key: string; label: string; roles: string[];
    /** The class's grade, and its section name (null: the whole grade). */
    grade?: { id: number; name: string }; section?: string | null;
    /** Homeroom and subjects in the same class: each hat on its own ('<key>:homeroom' / ':subjects'). */
    parts?: { key: string; label: string; hint: string }[];
  }[];
  view_as: string;
}

// ---- Exam Bank: released school exams any staff member may read and copy ----
export interface ExamBankRef { id: number; name: string }
/** Figures from the viewer's own students only. */
export interface ExamBankMyResults { took: number; avg_score: number | null }
export interface ExamBankRow {
  id: number;
  name: string;
  kind: ExamKind;
  grade: ExamBankRef | null;
  subject: ExamBankRef | null;
  question_count: number;
  copies_count: number;
  chapters: string[];
  creator: ExamBankRef;
  created: string;
  delivery: 'app' | 'paper';
  my_results: ExamBankMyResults;
}
export interface ExamBankListResponse {
  results: ExamBankRow[];
  count: number;
  next_page: number | null;
  facets: { grades: ExamBankRef[]; subjects: ExamBankRef[] };
}
export interface ExamBankParams {
  grade?: number; subject?: number; kind?: ExamKind; search?: string; page?: number; page_size?: number;
}
export interface ExamBankDetail extends Omit<AuthoringExam, 'status'> {
  creator: ExamBankRef;
  created: string;
  copies_count: number;
  question_count: number;
  time_minutes?: number | null;
  instructions?: string;
  delivery?: 'app' | 'paper';
  my_results: ExamBankMyResults;
}

/** One question the AI read from an uploaded file: not saved until the teacher adds it. */
export interface ImportedQuestion {
  question: QuestionPayload & { answer?: string | null };
  /** 'document' = the file gave the answer; 'ai' = the AI worked it out (check it). */
  answer_source: 'document' | 'ai';
  note: string;
  /** Why it can't be added as is (fix it in the editor first). */
  problems: string[];  /** Set once the AI has worked out the chapter (shown in the review). */
  chapter?: AnnotateResult['chapter'];
}

/** Where a question comes from: its chapter (any grade's chapter of the subject) and textbook reference. */
export interface QuestionSource { chapter_id?: number | null; reference?: string }

export type QuestionPayload = QuestionSource & (
  | { type: 'multiple_choice'; text: string; options: string[]; answer: string; explanation?: string }
  | { type: 'fill_blank'; text: string; blanks: string[][]; explanation?: string }
  | { type: 'matching'; text: string; left: string[]; right: string[]; pairs: [number, number][]; explanation?: string }
  | { type: 'written'; text: string; max_marks: number; model_answer: string; notes: string; explanation?: string });

/** A subject book the AI can draw chapters, explanations and references from. */
export interface AnnotateBook {
  id: number;
  title: string;
  grades: { id: number; name: string }[];
  /** The book has a search index; one without cannot be searched yet. */
  indexed: boolean;
  /** One of the worksheet grade's books (picked by default). */
  for_exam_grade: boolean;
}

export interface AnnotateResult {
  chapter: { id: number; name: string; grade: string } | null;
  explanation: string;
  reference: string;
}

export interface AuthoringChapter { id: number; name: string; grades: string[] }

export interface PublishProblem { question_id: number | null; code: string; message: string }

// ---- Written-answer marking (school mode phase 3c-i) ----
export type WrittenAiStatus = 'pending' | 'done' | 'failed' | 'skipped';
export type MarkingStatus = 'to_mark' | 'marked';

export interface MarkingAnswer {
  id: number;
  student: { id: number; name: string };
  text: string;
  /** WrittenMark snapshot: the question's max marks when the student submitted. */
  max_marks?: number;
  ai_status: WrittenAiStatus;
  ai_marks: number | null;
  ai_reason: string;
  /** Why the AI didn't mark (ai_status 'skipped'): 'ai_limit' or an openrouter_* code. */
  ai_skip_reason?: string;
  marks: number | null;
  marked_by: string | null;
  marked_at: string | null;
  /** Photo answers: authenticated URLs, fetched through the API instance. */
  photos?: { id: number; url: string }[];
}

export interface MarkingQuestion {
  question: { id: number; text: string; model_answer: string; notes: string; max_marks: number };
  answers: MarkingAnswer[];
}

export interface ExamMarkingQuestionRow {
  question_id: number; n: number; text_excerpt: string; max_marks: number;
  to_mark: number; marked: number; ai_pending: number;
}

export interface ExamMarking { questions: ExamMarkingQuestionRow[]; total_to_mark: number }

export interface MarkingSummary {
  total_to_mark: number;
  exams: { id: number; name: string; kind: ExamKind; to_mark: number; oldest: string }[];
}

export interface ConfirmMarkResult {
  mark: MarkingAnswer;
  result: { awaiting_marking: boolean; score_percentage: number | null };
  next_id: number | null;
}

// ---- Student 360 academics (exam breakdown, patterns, national readiness) ----
export type ExamQuestionKind = 'right' | 'gap' | 'hard_for_all' | 'missed' | 'blank';
export type Difficulty = 'easy' | 'medium' | 'hard';
export interface ExamBreakdownQuestion {
  number: number;
  question_id: number;
  type: string;
  text: string;
  chapter: { id: number; name: string } | null;
  difficulty: Difficulty;
  is_correct: boolean;
  credit: number | null;
  student_answer: string | null;
  correct_answer: string | null;
  options: string[] | null;
  class_correct_pct: number | null;
  kind: ExamQuestionKind;
  class_top_wrong: { answer: string; pct: number | null } | null;
  common_wrong: boolean;
  time_s: number | null;
  class_time_s: number | null;
  pace: 'fast' | 'slow' | null;
  written: { marks: number | null; max_marks: number } | null;
}
export interface StudentExamBreakdown {
  result: { id: number; created: string; source: 'app' | 'paper' | string; score_percentage: number | null;
    right: number; wrong: number; unanswered: number; awaiting_marking: boolean; time_taken_s: number | null;
    late?: boolean; late_unverified?: boolean };
  exam: { id: number; name: string; kind: string; delivery: string; subject: string | null; questions: number };
  class: { students: number; average: number | null; median: number | null; rank: number | null; percentile: number | null };
  previous: { exam_name: string; score_percentage: number | null; delta: number | null } | null;
  summary: Record<ExamQuestionKind | 'common_wrong' | 'fast_wrong' | 'slow_wrong', number>;
  time: { student_avg_s: number | null; class_avg_s: number | null };
  by_chapter: { chapter_id: number | null; name: string; total: number; right: number; pct: number | null; class_pct: number | null }[];
  by_difficulty: { level: Difficulty; total: number; right: number; pct: number | null }[];
  questions: ExamBreakdownQuestion[];
}
export interface ScoreQuartiles { min: number; p25: number; median: number; p75: number; max: number; mean: number; stdev: number; n: number }
export interface TaskCompletion { assigned: number; done: number; late: number; missing: number; avg_score: number | null }
export interface StudentPatterns {
  exams: number;
  spread: { student: ScoreQuartiles | null; class: ScoreQuartiles | null; consistency: 'steady' | 'variable' | 'up_and_down' | null };
  ranks: { date: string; exam: string; score: number | null; rank: number | null; of: number; percentile: number | null }[];
  repeat_misses: { chapter_id: number; name: string; exams_missed: number; missed: number; total: number; pct_missed: number | null }[];
  difficulty_by_month: ({ month: string } & Record<Difficulty, { right: number; total: number; pct: number | null }>)[];
  blanks: { first: number; middle: number; last: number; total: number; end_heavy: boolean };
  practice: { practice_count: number; practice_avg: number | null; exam_count: number; exam_avg: number | null };
  tasks: Record<'worksheet' | 'homework' | 'exam', TaskCompletion>;
}
export interface ReadinessChapter { chapter_id: number; name: string; share: number; trend: string | null; attempts: number; mastery: number | null }
export interface StudentNationalReadiness {
  available: boolean;
  national_exam: { id: number; name: string } | null;
  subjects: { subject_id: number; name: string; readiness: number | null; coverage: number | null; papers: number | null;
    chapters: ReadinessChapter[]; focus: ReadinessChapter[] }[];
}

// ----- Staff "My AI" wallet (backend api/school_panel/serializers/ai_wallet.py) -----
export interface AiWalletBlock {
  balance_credits: string;
  has_key: boolean;
  key_last4: string | null;
  key_added_at: string | null;
  use_own_key_first: boolean;
}
export interface AiSchoolBlock {
  allocation_credits: number;
  used_credits: number;
  left_credits: number;
  resets_on: string;
  exhausted: boolean;
}
export interface AiPurchaseRow {
  tx_ref: string; credits: number; amount_etb: string; status: string; created: string;
}
export interface AiBundle { id: string; credits: number; price_etb: number | string; popular?: boolean }
export interface AiBreakdown {
  by_feature: { feature: string; credits: number }[];
  by_staff: { user_id: number; name: string; credits: number }[];
}
export interface AiWallet {
  school: AiSchoolBlock | null;
  wallet: AiWalletBlock;
  purchases: AiPurchaseRow[];
  bundles: AiBundle[];
  purchases_enabled: boolean;
  /** School admins only. */
  breakdown?: AiBreakdown;
}
export interface AiCheckoutResponse { tx_ref: string; checkout_url: string }
export type AiVerifyStatus = 'verified' | 'already_verified' | 'pending' | 'failed';
export interface AiVerifyResponse { status: AiVerifyStatus; wallet: AiWalletBlock }
/** Own-key failures: openrouter_invalid_key | openrouter_daily_limit | openrouter_busy. */
export interface OpenRouterKeyErrorBody { code: string; detail: string }
